import {
  readConfig,
  getCustomerKey,
  getSessionKey,
  getTouchpoint,
  createProductCardRenderer,
  fetchSearchPage,
  fetchAutocomplete,
  syncCustomerKey,
  waitForConsent,
  sendClickNotification,
  escapeHtml,
  escapeAttr,
  safeUrl,
  normalizeSortValue,
  parseSortOptions,
  parseSortLabels,
  buildSwatchStyle,
  buildFacetColorStyle,
  translateFacetLabel,
  hasVoyadoConsent,
} from 'voyado-elevate';

const _syncOnce = syncCustomerKey();
let _loadedWithoutConsent = false;

const DEFAULT_SEARCH_SORTS = ['RELEVANCE', 'PRICE_ASCENDING', 'PRICE_DESCENDING', 'NAME'];

function parsePipeList(value) {
  return String(value ?? '')
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
}

function readSearchResultsConfig() {
  const element = document.getElementById('voyado-search-results-config');
  const data = element ? element.dataset : {};
  const secondaryListThreshold = parseInt(data.secondaryListThreshold || '0', 10);

  return {
    channels: data.channels || '',
    priceId: data.priceId || '',
    stores: data.stores || '',
    templateId: data.templateId || '',
    viewId: data.viewId || 'production',
    presentCustom: data.presentCustom || '',
    presentPrices: data.presentPrices || '',
    productRules: data.productRules || '',
    notify: data.notify === 'true',
    productsPerPage: Math.max(parseInt(data.productsPerPage || '24', 10) || 24, 1),
    enableInfiniteScroll: data.enableInfiniteScroll === 'true',
    columnsDesktop: parseInt(data.columnsDesktop || '4', 10) || 4,
    columnsMobile: parseInt(data.columnsMobile || '2', 10) || 2,
    showPrice: data.showPrice !== 'false',
    showImage: data.showImage !== 'false',
    imageAspectRatio: data.imageAspectRatio || 'natural',
    enableImageSlider: data.enableImageSlider === 'true',
    showFacets: data.showFacets !== 'false',
    facetsPosition: data.facetsPosition || 'sidebar',
    facetsLabel: data.facetsLabel || 'Filter',
    activeFacetsLabel: data.activeFacetsLabel || 'Aktive Filter',
    clearAllLabel: data.clearAllLabel || 'Alle zurücksetzen',
    showSort: data.showSort !== 'false',
    sortLabel: data.sortLabel || 'Sortierung',
    sortOptions: parseSortOptions(data.sortOptions || '', DEFAULT_SEARCH_SORTS),
    sortLabels: parseSortLabels(data.sortLabels || ''),
    defaultSortOrder: normalizeSortValue(data.defaultSortOrder || ''),
    totalHitsLabel: data.totalHitsLabel || '{n} Ergebnisse',
    totalHitsLabelOne: data.totalHitsLabelOne || '1 Ergebnis',
    resultsHeadingLabel: data.resultsHeadingLabel || 'Suchergebnisse fuer "{query}"',
    enableDidYouMean: data.enableDidYouMean !== 'false',
    didYouMeanLabel: data.didYouMeanLabel || 'Zeige Ergebnisse für "{suggestion}" statt "{original}"',
    didYouMeanLinkLabel: data.didYouMeanLinkLabel || 'Meinten Sie: "{suggestion}"?',
    enableOriginalQueryLink: data.enableOriginalQueryLink !== 'false',
    originalQueryLinkLabel: data.originalQueryLinkLabel || 'Stattdessen Ergebnisse für "{original}" anzeigen',
    enableSecondaryList: data.enableSecondaryList !== 'false',
    secondaryListThreshold: Number.isFinite(secondaryListThreshold) ? Math.max(secondaryListThreshold, 0) : 0,
    secondaryListLabel: data.secondaryListLabel || 'Ahnliche Produkte',
    secondaryListSubtext: data.secondaryListSubtext || 'Keine exakten Treffer - das koennte dir gefallen:',
    secondaryListViewAllLabel: data.secondaryListViewAllLabel || 'Alle ähnlichen Produkte anzeigen',
    enableRecentSearches: data.enableRecentSearches !== 'false',
    recentSearchesLabel: data.recentSearchesLabel || 'Letzte Suchen',
    trendingLabel: data.trendingLabel || 'Trendsuchen',
    recentSearchesMax: parseInt(data.recentSearchesMax || '8', 10) || 8,
    enableRecentlyViewed: data.enableRecentlyViewed !== 'false',
    recentlyViewedLabel: data.recentlyViewedLabel || 'Zuletzt angesehen',
    recentlyViewedMax: parseInt(data.recentlyViewedMax || '4', 10) || 4,
    enableContentList: data.enableContentList !== 'false',
    contentListId: data.contentListId || 'content',
    contentListLabel: data.contentListLabel || 'Inhalte',
    contentTypeLabels: Object.assign(
      { category: 'Kategorien', page: 'Seiten', article: 'Artikel', blog: 'Blog' },
      parseSortLabels(data.contentTypeLabels || '')
    ),
    enableRelatedSearches: data.enableRelatedSearches !== 'false',
    relatedSearchesLabel: data.relatedSearchesLabel || 'Verwandte Suchanfragen',
    relatedSearchesMax: parseInt(data.relatedSearchesMax || '6', 10) || 6,
    productsTabLabel: data.productsTabLabel || 'Produkte',
    contentTabLabel: data.contentTabLabel || 'Inhalte',
    contentTabBadge: data.contentTabBadge !== 'false',
    noResultsLabel: data.noResultsLabel || 'Keine Ergebnisse gefunden',
    noResultsSubtext: data.noResultsSubtext || 'Bitte versuche einen anderen Suchbegriff.',
    noResultsShowSearchBar: data.noResultsShowSearchBar !== 'false',
    enableRedirect: data.enableRedirect !== 'false',
    errorLabel: data.errorLabel || 'Die Suche ist momentan nicht verfuegbar',
    errorSubtext: data.errorSubtext || 'Bitte versuche es in wenigen Augenblicken erneut.',
    loadingLabel: data.loadingLabel || 'Suche wird geladen',
    rangeApplyLabel: data.rangeApplyLabel || 'Anwenden',
    loadMoreLabel: data.loadMoreLabel || 'Mehr laden',
    boolTrueLabel: data.boolTrueLabel || 'Ja',
    boolFalseLabel: data.boolFalseLabel || 'Nein',
  };
}

function createEmptyState() {
  return {
    query: '',
    sort: '',
    filters: {},
    page: 1,
    bypassCorrection: false,
    origin: '',
    viewAllSecondary: false,
  };
}

class VoyadoSearchPage {
  constructor() {
    this._root = document.querySelector('[data-voyado-search-results]');
    if (!this._root) return;

    try {
      this._baseConfig = readConfig();
    } catch {
      return;
    }

    this._sectionConfig = readSearchResultsConfig();
    Object.assign(this, createProductCardRenderer(() => this._sectionConfig, {
      buildSwatchStyleFn: buildSwatchStyle,
      includeImageFallbackSwatch: false,
    }));
    this._searchForm = this._root.querySelector('#voyado-search-form');
    this._searchInput = this._root.querySelector('input[name="q"]');
    this._resultsSummary = this._root.querySelector('#voyado-search-results-summary');
    this._resultsSummaryText = this._resultsSummary?.querySelector('.h2') || null;
    this._facetsContainer = this._root.querySelector('#voyado-facets');
    this._sortContainer = this._root.querySelector('#voyado-sort');
    this._sortWrap = this._root.querySelector('#voyado-sort-container');
    this._sortCurrentLabel = this._root.querySelector('#voyado-sort-current-label');
    this._toolbar = this._root.querySelector('#voyado-toolbar');
    this._activeFiltersContainer = this._root.querySelector('#voyado-active-filters');
    this._totalHitsContainer = this._root.querySelector('#voyado-total-hits');
    this._didYouMean = this._root.querySelector('#voyado-did-you-mean');
    this._grid = this._root.querySelector('#voyado-product-grid');
    if (this._grid && !this._grid.reveal) this._grid.reveal = () => {};
    this._loading = this._root.querySelector('#voyado-loading');
    this._sentinel = this._root.querySelector('#voyado-sentinel');
    this._noResults = this._root.querySelector('#voyado-no-results');
    this._error = this._root.querySelector('#voyado-error');
    this._pagination = this._root.querySelector('#voyado-pagination');
    this._secondaryList = this._root.querySelector('#voyado-secondary-list');
    this._contentList = this._root.querySelector('#voyado-content-list');
    this._relatedSearches = this._root.querySelector('#voyado-related-searches');
    this._tabs = this._root.querySelector('#voyado-search-tabs');
    this._recentSearches = this._root.querySelector('#voyado-recent-searches');
    this._recentlyViewed = this._root.querySelector('#voyado-recently-viewed');
    this._status = this._root.querySelector('#voyado-search-status');
    this._observer = null;
    this._abortController = null;
    this._emptyStateAbortController = null;
    this._emptyStateData = null;
    this._fetchGeneration = 0;
    this._isFetching = false;
    this._hasNextPage = false;
    this._shouldMoveFocus = false;
    this._state = createEmptyState();
    this._facets = [];
    this._availableSorts = [];
    this._selectedFilters = [];
    this._totalHits = 0;
    this._currentPage = 1;
    this._totalPages = 1;
    this._loadedCount = 0;
    this._lastContentCount = 0;
    this._activeTab = 'products';
    this._contentItems = [];
    this._activeContentType = 'all';
    this._hasContentSubtabs = false;
    this._isLoadingVisible = false;
    this._isShowingNoResults = false;
    this._isShowingError = false;
    this._facetOpenState = {};
    if (!this._searchForm || !this._searchInput || !this._grid || !this._sentinel) {
      return;
    }

    this._bindEvents();
    this._state = this._readStateFromUrl();
    this._applyStateToSearchInput();
    this._renderHeaderState();
    this._replaceHistory();
    this._loadFromState('replace');
  }

  _bindEvents() {
    this._searchForm.addEventListener('submit', (event) => {
      event.preventDefault();

      this._state.query = this._searchInput.value.trim();
      this._state.page = 1;
      this._state.bypassCorrection = false;
      this._state.origin = '';
      this._shouldMoveFocus = true;
      this._renderHeaderState();
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._sortContainer?.addEventListener('click', (event) => {
      const btn = event.target instanceof HTMLElement ? event.target.closest('[data-sort-value]') : null;
      if (!(btn instanceof HTMLElement)) return;

      this._state.sort = normalizeSortValue(btn.dataset.sortValue);
      this._state.page = 1;
      this._shouldMoveFocus = true;
      this._sortContainer.dispatchEvent(new CustomEvent('listbox:select', { bubbles: true }));
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._facetsContainer?.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;

      const facetId = target.dataset.facetId;
      if (!facetId) return;

      if (target.type === 'checkbox') {
        const current = Array.isArray(this._state.filters[facetId]) ? [...this._state.filters[facetId]] : [];

        if (target.checked) {
          if (!current.includes(target.value)) current.push(target.value);
          this._state.filters[facetId] = current;
        } else {
          const next = current.filter((value) => value !== target.value);
          if (next.length > 0) {
            this._state.filters[facetId] = next;
          } else {
            delete this._state.filters[facetId];
          }
        }

        this._state.page = 1;
        this._shouldMoveFocus = true;
        this._pushHistory();
        this._loadFromState('replace');
      }
    });

    this._facetsContainer?.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('[data-range-form]')) return;

      event.preventDefault();
      const facetId = form.dataset.facetId;
      if (!facetId) return;

      const minInput = form.querySelector('[data-range-min]');
      const maxInput = form.querySelector('[data-range-max]');
      const minValue = minInput instanceof HTMLInputElement ? minInput.value.trim() : '';
      const maxValue = maxInput instanceof HTMLInputElement ? maxInput.value.trim() : '';
      const nextRange = {};

      if (minValue !== '') nextRange.min = minValue;
      if (maxValue !== '') nextRange.max = maxValue;

      if (Object.keys(nextRange).length > 0) {
        this._state.filters[facetId] = nextRange;
      } else {
        delete this._state.filters[facetId];
      }

      this._state.page = 1;
      this._shouldMoveFocus = true;
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._facetsContainer?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const toggle = target.closest('[data-facet-toggle]');
      if (toggle) {
        const facetId = toggle.getAttribute('data-facet-toggle');
        if (!facetId) return;

        this._facetOpenState[facetId] = !this._isFacetOpen(facetId);
        this._renderFacets();
        return;
      }

      const clearButton = target.closest('[data-action="clear-all-filters"]');
      if (clearButton) {
        this._state.filters = {};
        this._state.page = 1;
        this._shouldMoveFocus = true;
        this._pushHistory();
        this._loadFromState('replace');
      }
    });

    this._activeFiltersContainer?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const clearButton = target.closest('[data-action="clear-all-filters"]');
      if (clearButton) {
        this._state.filters = {};
        this._state.page = 1;
        this._shouldMoveFocus = true;
        this._pushHistory();
        this._loadFromState('replace');
        return;
      }

      const removeButton = target.closest('[data-remove-filter]');
      if (!removeButton) return;

      const facetId = removeButton.getAttribute('data-facet-id');
      if (!facetId) return;

      const rangeKey = removeButton.getAttribute('data-range-key');
      if (rangeKey) {
        const currentRange = this._state.filters[facetId];
        if (currentRange && !Array.isArray(currentRange)) {
          const nextRange = { ...currentRange };
          delete nextRange[rangeKey];

          if (Object.keys(nextRange).length > 0) {
            this._state.filters[facetId] = nextRange;
          } else {
            delete this._state.filters[facetId];
          }
        }
      } else {
        const value = removeButton.getAttribute('data-filter-value');
        const current = this._state.filters[facetId];

        if (Array.isArray(current) && value) {
          const next = current.filter((item) => item !== value);
          if (next.length > 0) {
            this._state.filters[facetId] = next;
          } else {
            delete this._state.filters[facetId];
          }
        }
      }

      this._state.page = 1;
      this._shouldMoveFocus = true;
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._pagination?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const pageButton = target.closest('[data-page]');
      if (!pageButton) return;

      const nextPage = parseInt(pageButton.getAttribute('data-page') || '', 10);
      if (!Number.isFinite(nextPage) || nextPage < 1 || nextPage === this._state.page) return;

      this._state.page = nextPage;
      this._shouldMoveFocus = true;
      this._pushHistory();
      this._loadFromState('replace');
    });

    this._bindProductCardContainer(this._grid);
    this._bindProductCardContainer(this._secondaryList);
    this._bindProductCardContainer(this._recentlyViewed);

    this._tabs?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const button = target.closest('[data-tab]');
      if (!(button instanceof HTMLButtonElement)) return;

      const tab = button.dataset.tab;
      if (!tab || tab === this._activeTab) return;

      this._activeTab = tab;
      this._applyTabVisibility();
      this._renderTabs(this._lastContentCount);
    });

    this._contentList?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      // Handle sub-tab filter clicks
      const subtabButton = target.closest('[data-content-type]');
      if (subtabButton instanceof HTMLButtonElement) {
        const contentType = subtabButton.dataset.contentType ?? 'all';
        if (contentType !== this._activeContentType) {
          this._activeContentType = contentType;
          // Update active state on buttons
          const wrapper = this._contentList.querySelector('.voyado-search__content-cards-wrapper');
          if (wrapper) {
            wrapper.innerHTML = this._renderContentCards(this._contentItems);
          }
          this._contentList.querySelectorAll('[data-content-type]').forEach((btn) => {
            const isActive = btn.dataset.contentType === contentType;
            btn.classList.toggle('voyado-search__content-subtab--active', isActive);
            btn.setAttribute('aria-selected', String(isActive));
          });
        }
        return;
      }

      const link = target.closest('a[data-voyado-ticket]');
      if (!(link instanceof HTMLAnchorElement)) return;

      const ticket = link.dataset.voyadoTicket;
      if (ticket) {
        sendClickNotification(this._baseConfig.cluster, this._baseConfig.market, ticket);
      }
    });

    this._didYouMean?.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const button = target.closest('[data-bypass-correction]');
      if (!button) return;

      this._state.bypassCorrection = true;
      this._state.page = 1;
      this._shouldMoveFocus = true;
      this._state.origin = 'UNDO_AUTO_CORRECT';
      this._pushHistory();
      this._loadFromState('replace');
    });

    window.addEventListener('popstate', () => {
      this._state = this._readStateFromUrl();
      this._applyStateToSearchInput();
      this._renderHeaderState();
      this._loadFromState('replace');
    });
  }

  _readStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const state = createEmptyState();

    state.query = params.get('q')?.trim() || this._searchInput.value.trim();
    state.sort = normalizeSortValue(params.get('sort') || this._sectionConfig.defaultSortOrder || '');
    state.bypassCorrection = params.get('bypass_correction') === '1';
    state.origin = params.get('origin') || '';
    state.viewAllSecondary = params.get('viewAllSecondary') === 'true';

    const pageValue = parseInt(params.get('page') || '1', 10);
    state.page = Number.isFinite(pageValue) && pageValue > 0 ? pageValue : 1;

    for (const [key, value] of params.entries()) {
      if (!key.startsWith('f.')) continue;

      const rawKey = key.slice(2);

      if (rawKey.endsWith('.min')) {
        const facetId = rawKey.slice(0, -4);
        const current = state.filters[facetId];
        if (!current || Array.isArray(current)) state.filters[facetId] = {};
        state.filters[facetId].min = value;
        continue;
      }

      if (rawKey.endsWith('.max')) {
        const facetId = rawKey.slice(0, -4);
        const current = state.filters[facetId];
        if (!current || Array.isArray(current)) state.filters[facetId] = {};
        state.filters[facetId].max = value;
        continue;
      }

      const values = value
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean);

      if (values.length > 0) {
        state.filters[rawKey] = values;
      }
    }

    return state;
  }

  _applyStateToSearchInput() {
    this._searchInput.value = this._state.query;
  }

  _renderHeaderState() {
    const hasQuery = Boolean(this._state.query);

    if (this._searchForm) {
      this._searchForm.hidden = hasQuery;
    }

    if (this._resultsSummary) {
      this._resultsSummary.hidden = !hasQuery;
    }

    if (hasQuery && this._resultsSummaryText) {
      this._resultsSummaryText.textContent = this._sectionConfig.resultsHeadingLabel.replace('{query}', this._state.query);
    }
  }

  _buildUrlSearchParams(pageOverride = this._state.page) {
    const params = new URLSearchParams();

    if (this._state.query) {
      params.set('q', this._state.query);
    }

    if (this._state.sort) {
      params.set('sort', this._state.sort);
    }

    Object.entries(this._state.filters).forEach(([facetId, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(`f.${facetId}`, value.join('|'));
        }
        return;
      }

      if (value.min) params.set(`f.${facetId}.min`, value.min);
      if (value.max) params.set(`f.${facetId}.max`, value.max);
    });

    if (pageOverride > 1) {
      params.set('page', String(pageOverride));
    }

    if (this._state.bypassCorrection) {
      params.set('bypass_correction', '1');
    }

    if (this._state.origin) {
      params.set('origin', this._state.origin);
    }

    if (this._state.viewAllSecondary) {
      params.set('viewAllSecondary', 'true');
    }

    return params;
  }

  _pushHistory() {
    const params = this._buildUrlSearchParams();
    const queryString = params.toString();
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    history.pushState({}, '', nextUrl);
  }

  _replaceHistory(pageOverride = this._state.page) {
    const params = this._buildUrlSearchParams(pageOverride);
    const queryString = params.toString();
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    history.replaceState({}, '', nextUrl);
  }

  async _loadFromState(historyMode) {
    this._disconnectObserver();
    this._abortInFlight();
    this._abortEmptyStateFetch();
    this._clearUiStates();
    this._clearResults();
    this._loadedCount = 0;
    this._currentPage = 1;
    this._totalPages = 1;
    this._hasNextPage = false;
    this._totalHits = 0;
    this._facets = [];
    this._availableSorts = [];
    this._selectedFilters = [];

    if (!this._state.query) {
      if (this._sortContainer) {
        this._sortContainer.innerHTML = '';
      }
      if (this._sortWrap) {
        this._sortWrap.hidden = true;
      }
      if (this._facetsContainer) {
        this._facetsContainer.innerHTML = '';
        this._facetsContainer.hidden = true;
      }
      if (this._activeFiltersContainer) {
        this._activeFiltersContainer.innerHTML = '';
        this._activeFiltersContainer.hidden = true;
      }
      if (this._totalHitsContainer) {
        this._totalHitsContainer.textContent = '';
      }
      this._renderPagination();
      if (this._sectionConfig.enableRecentSearches || this._sectionConfig.enableRecentlyViewed) {
        if (this._emptyStateData) {
          this._renderRecentSearches(this._emptyStateData);
          this._renderRecentlyViewed(this._emptyStateData);
        } else {
          await this._fetchEmptyState();
        }
      }
      if (historyMode === 'replace') this._replaceHistory(1);
      this._focusResultsIfNeeded();
      return;
    }

    const targetPage = Math.max(this._state.page, 1);

    if (!this._sectionConfig.enableInfiniteScroll) {
      const response = await this._fetchPage(targetPage, false);
      if (!response) return;
    } else {
      for (let page = 1; page <= targetPage; page += 1) {
        const append = page > 1;
        const response = await this._fetchPage(page, append);
        if (!response) return;
      }
    }

    if (historyMode === 'replace') {
      this._replaceHistory(this._currentPage);
    }

    if (this._sectionConfig.enableInfiniteScroll && this._currentPage < this._totalPages) {
      this._connectObserver();
    }

    this._focusResultsIfNeeded();
  }

  async _fetchPage(page, append) {
    await _syncOnce;
    await waitForConsent();
    if (append && this._isFetching) return null;

    const generation = ++this._fetchGeneration;
    const controller = new AbortController();
    this._abortController = controller;
    this._isFetching = append;
    const pageSize = this._getPageSize();

    const skip = (page - 1) * pageSize;
    const params = {
      market: this._baseConfig.market,
      customerKey: getCustomerKey(),
      sessionKey: getSessionKey(),
      touchpoint: getTouchpoint(),
      locale: this._baseConfig.locale,
      q: this._state.query,
      skip: String(skip),
      limit: String(pageSize),
    };

    if (this._sectionConfig.channels) params.channels = this._sectionConfig.channels;
    if (this._sectionConfig.priceId) params.priceId = this._sectionConfig.priceId;
    if (this._sectionConfig.stores) params.stores = this._sectionConfig.stores;
    if (this._sectionConfig.templateId) params.templateId = this._sectionConfig.templateId;
    if (this._sectionConfig.viewId && this._sectionConfig.viewId !== 'production') params.viewId = this._sectionConfig.viewId;
    if (new URLSearchParams(location.search).get('PREVIEW') === 'true') params.viewId = 'preview';
    if (this._sectionConfig.presentCustom) params.presentCustom = this._sectionConfig.presentCustom;
    if (this._sectionConfig.presentPrices) params.presentPrices = this._sectionConfig.presentPrices;
    if (this._sectionConfig.productRules) params.productRules = this._sectionConfig.productRules;
    if (this._sectionConfig.notify) params.notify = 'false';
    if (this._state.sort) params.sort = this._state.sort;
    if (this._state.bypassCorrection) params.spellCheck = 'false';
    if (this._state.origin) params.origin = this._state.origin;
    if (this._state.viewAllSecondary) params.viewAllSecondary = 'true';

    Object.entries(this._state.filters).forEach(([facetId, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) params[`f.${facetId}`] = value.join('|');
        return;
      }

      if (value.min) params[`f.${facetId}.min`] = String(value.min);
      if (value.max) params[`f.${facetId}.max`] = String(value.max);
    });

    const body = {};
    if (this._sectionConfig.enableContentList && this._sectionConfig.contentListId) {
      body.contentLists = [{ id: this._sectionConfig.contentListId, limit: 200 }];
    }

    this._setLoading(true, !append);

    try {
      const data = await fetchSearchPage(this._baseConfig.cluster, params, body, controller.signal);

      if (generation !== this._fetchGeneration) return null;

      if (!hasVoyadoConsent()) {
        _loadedWithoutConsent = true;
      }

      this._renderResponse(data, page, append);
      return data;
    } catch (error) {
      if (error.name === 'AbortError') return null;

      if (generation === this._fetchGeneration) {
        this._showError();
      }

      return null;
    } finally {
      if (append) {
        this._isFetching = false;
      }

      if (generation === this._fetchGeneration) {
        this._setLoading(false, false);
      }
    }
  }

  _renderResponse(data, page, append) {
    const primaryList = this._getPrimaryList(data);
    const productGroups = this._getPrimaryProductGroups(data, primaryList);

    if (!append) {
      const redirectUrl = this._getRedirectUrl(data);
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
    }

    if (!append) {
      const sortOptions = this._getSortOptions(primaryList);

      this._facets = this._getFacets(primaryList);
      this._availableSorts = sortOptions
        .map((option) => normalizeSortValue(option?.id ?? option))
        .filter(Boolean);
      this._selectedFilters = Array.isArray(primaryList?.selectedFilters)
        ? primaryList.selectedFilters
        : Array.isArray(data?.selectedFilters)
          ? data.selectedFilters
          : [];

      if (!this._state.sort) {
        this._state.sort = this._getSelectedSort(primaryList);
      }

      this._totalHits = this._resolveTotalHits(data, primaryList, productGroups, page);
      this._totalPages = this._resolveTotalPages(primaryList, productGroups.length, page);
      this._hasNextPage = this._resolveHasNextPage(primaryList, productGroups.length, page);

      this._renderSort();
      this._renderFacets();
      this._renderTotalHits();
      if (this._toolbar) this._toolbar.hidden = false;
    }

    if (!append) {
      this._clearResults();
    }

    const cardsHtml = productGroups
      .map((group) => this._renderProductCard(group))
      .filter(Boolean)
      .join('');

    if (cardsHtml) {
      this._grid.insertAdjacentHTML('beforeend', cardsHtml);
    }

    this._loadedCount += productGroups.length;
    this._currentPage = page;
    this._state.page = page;

    this._renderActiveFilters();
    this._renderPagination();

    if (!append) {
      const dymSuggestion = this._getDidYouMeanSuggestion(data);
      const dymOrigin = dymSuggestion ? this._getDYMOrigin(data) : '';
      this._renderDidYouMean(dymSuggestion, this._state.query, dymOrigin);
      if (dymSuggestion && dymOrigin === 'UNDO_AUTO_CORRECT' && this._resultsSummaryText) {
        this._resultsSummaryText.textContent = this._sectionConfig.resultsHeadingLabel.replace('{query}', dymSuggestion);
      }
      this._renderSecondaryList(this._getSecondaryList(data, primaryList), this._totalHits);
      this._renderContentList(data);
      this._renderRelatedSearches(data);

      if (productGroups.length === 0) {
        this._showNoResults();
        if (this._sectionConfig.enableInfiniteScroll) {
          this._disconnectObserver();
        }
        this._sentinel.hidden = true;
        return;
      }
    }

    if (this._sectionConfig.enableInfiniteScroll) {
      const shouldObserve = this._hasNextPage && productGroups.length > 0;
      this._sentinel.hidden = !shouldObserve;
      if (shouldObserve) {
        this._connectObserver();
      } else {
        this._disconnectObserver();
      }

      this._replaceHistory(this._currentPage);
      return;
    }

    this._sentinel.hidden = true;
  }

  _sanitiseHighlight(text) {
    if (typeof text !== 'string') return '';

    return text
      .replace(/[&<>"]/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
      }[char]))
      .replace(/&lt;(\/?)em&gt;/g, '<$1em>')
      .replace(/\{([^}]+)\}/g, '<em>$1</em>');
  }

  _abortEmptyStateFetch() {
    if (!this._emptyStateAbortController) return;
    this._emptyStateAbortController.abort();
    this._emptyStateAbortController = null;
  }

  async _fetchEmptyState() {
    this._abortEmptyStateFetch();
    await waitForConsent();
    this._emptyStateAbortController = new AbortController();

    const params = {
      market: this._baseConfig.market,
      customerKey: getCustomerKey(),
      sessionKey: getSessionKey(),
      touchpoint: getTouchpoint(),
      locale: this._baseConfig.locale,
      q: '',
    };

    if (this._sectionConfig.channels) params.channels = this._sectionConfig.channels;
    if (this._sectionConfig.priceId) params.priceId = this._sectionConfig.priceId;
    if (this._sectionConfig.stores) params.stores = this._sectionConfig.stores;
    if (this._sectionConfig.templateId) params.templateId = this._sectionConfig.templateId;
    if (this._sectionConfig.viewId && this._sectionConfig.viewId !== 'production') params.viewId = this._sectionConfig.viewId;
    if (new URLSearchParams(location.search).get('PREVIEW') === 'true') params.viewId = 'preview';
    if (this._sectionConfig.presentCustom) params.presentCustom = this._sectionConfig.presentCustom;
    if (this._sectionConfig.presentPrices) params.presentPrices = this._sectionConfig.presentPrices;
    if (this._sectionConfig.notify) params.notify = 'false';

    try {
      const data = await fetchAutocomplete(this._baseConfig.cluster, params, {}, this._emptyStateAbortController.signal);

      if (this._state.query) {
        return null;
      }

      this._emptyStateData = data;
      this._renderRecentSearches(data);
      this._renderRecentlyViewed(data);
      return data;
    } catch (error) {
      if (error.name !== 'AbortError') {
        this._renderRecentSearches(null);
        this._renderRecentlyViewed(null);
      }

      return null;
    } finally {
      this._emptyStateAbortController = null;
    }
  }

  _getPageSize() {
    return this._sectionConfig.productsPerPage;
  }

  _getPrimaryList(data) {
    return data?.primaryList || data?.productGroups || {};
  }

  _getPrimaryProductGroups(data, primaryList) {
    const candidates = [
      primaryList?.productGroups,
      primaryList?.result,
      data?.productGroups?.result,
    ];

    for (const candidate of candidates) {
      if (!Array.isArray(candidate)) continue;
      return candidate.filter((group) => this._getGroupProducts(group).length > 0);
    }

    return [];
  }

  _getFacets(primaryList) {
    const candidates = [
      primaryList?.facets,
      primaryList?.filters,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }

    return [];
  }

  _getSortOptions(primaryList) {
    if (Array.isArray(primaryList?.sort?.options)) {
      return primaryList.sort.options;
    }

    if (Array.isArray(primaryList?.sortOrders)) {
      return primaryList.sortOrders;
    }

    return [];
  }

  _getSelectedSort(primaryList) {
    const candidates = [
      primaryList?.sort?.selected,
      primaryList?.sortOrders?.find?.((item) => item?.selected)?.id,
      this._sectionConfig.defaultSortOrder,
    ];

    for (const candidate of candidates) {
      const value = normalizeSortValue(candidate || '');
      if (value) return value;
    }

    return '';
  }

  _getPagination(primaryList) {
    return primaryList?.pagination || primaryList?.pageInfo || {};
  }

  _resolveTotalHits(data, primaryList, renderedProducts, page) {
    const pagination = this._getPagination(primaryList);
    const candidates = [
      data?.totalHits,
      data?.total,
      data?.productGroups?.count,
      pagination?.total,
      primaryList?.totalHits,
      primaryList?.total,
      primaryList?.productCount,
    ];

    for (const candidate of candidates) {
      const value = Number(candidate);
      if (Number.isFinite(value) && value >= 0) {
        return value;
      }
    }

    return ((page - 1) * this._getPageSize()) + renderedProducts.length;
  }

  _resolveTotalPages(primaryList, renderedCount, page) {
    const pageSize = this._getPageSize();

    // API returns totalHits directly on primaryList (no pagination object)
    if (this._totalHits > 0) {
      return Math.max(Math.ceil(this._totalHits / pageSize), 1);
    }

    const hasMoreResults = this._resolveHasNextPage(primaryList, renderedCount, page);
    return hasMoreResults ? page + 1 : page;
  }

  _resolveHasNextPage(primaryList, renderedCount, page) {
    const pagination = this._getPagination(primaryList);

    if (typeof pagination?.hasNext === 'boolean') {
      return pagination.hasNext;
    }

    const total = Number(pagination?.total);
    if (Number.isFinite(total) && total >= 0) {
      return page * this._getPageSize() < total;
    }

    return renderedCount >= this._getPageSize();
  }

  _renderFacets() {
    if (!this._facetsContainer) return;

    if (!this._sectionConfig.showFacets || this._facets.length === 0) {
      this._facetsContainer.innerHTML = '';
      this._facetsContainer.hidden = true;
      return;
    }

    const facetsHtml = this._facets
      .map((facet) => this._renderFacetGroup(facet))
      .join('');

    this._facetsContainer.hidden = false;
    this._facetsContainer.innerHTML = `
      <div class="voyado-search__facets-groups">
        ${facetsHtml}
      </div>
    `;
  }

  _renderFacetGroup(facet) {
    const facetId = String(facet?.id || '');
    const facetLabel = String(facet?.label || facetId);
    const facetType = this._getFacetType(facet);
    const isOpen = this._isFacetOpen(facetId);

    let content = '';

    if (['CHECKBOX', 'COLOR', 'SIZE', 'TEXT'].includes(facetType)) {
      const values = this._getFacetValues(facet);
      content = `
        <div class="voyado-search__facet-values">
          ${values
            .map((value) => {
              const inputId = `voyado-facet-${facetId}-${value?.id ?? value?.value ?? ''}`.replace(/[^a-zA-Z0-9_-]/g, '-');
              const valueId = String(value?.id ?? value?.value ?? '');
              const label = translateFacetLabel(String(value?.label ?? valueId), this._sectionConfig.boolTrueLabel, this._sectionConfig.boolFalseLabel);
              const count = Number(value?.count ?? 0);
              const colorStyle = facetType === 'COLOR' ? buildFacetColorStyle(value) : '';
              const selected = parsePipeList(String(this._state.filters[facetId] || '')).includes(valueId) ||
                (Array.isArray(this._state.filters[facetId]) && this._state.filters[facetId].includes(valueId));
              const valueLabel = colorStyle
                ? `
                  <span class="voyado-search__facet-value-label">
                    <span class="voyado-search__facet-swatch" aria-hidden="true" style="${escapeAttr(colorStyle)}"></span>
                    <span>${escapeHtml(label)}</span>
                  </span>
                `
                : `<span>${escapeHtml(label)}</span>`;

              return `
                <label class="voyado-search__checkbox${colorStyle ? ' voyado-search__checkbox--color' : ''}" for="${escapeAttr(inputId)}">
                  <input id="${escapeAttr(inputId)}" type="checkbox" data-facet-id="${escapeAttr(facetId)}" value="${escapeAttr(valueId)}"${selected ? ' checked' : ''}>
                  ${valueLabel}
                  <span class="text-subdued">${escapeHtml(String(count))}</span>
                </label>
              `;
            })
            .join('')}
        </div>
      `;
    } else {
      const currentRange = !Array.isArray(this._state.filters[facetId]) ? (this._state.filters[facetId] || {}) : {};
      const minValue = currentRange.min || '';
      const maxValue = currentRange.max || '';
      const bounds = this._getRangeBounds(facet);
      const minBound = bounds.min;
      const maxBound = bounds.max;
      const minInputId = `voyado-range-min-${facetId}`.replace(/[^a-zA-Z0-9_-]/g, '-');
      const maxInputId = `voyado-range-max-${facetId}`.replace(/[^a-zA-Z0-9_-]/g, '-');

      content = `
        <form class="voyado-search__range-form" data-range-form data-facet-id="${escapeAttr(facetId)}">
          <div class="voyado-search__range-inputs">
            <label class="voyado-search__range-field" for="${escapeAttr(minInputId)}">
              <span class="sr-only">${escapeHtml(`${facetLabel} Minimum`)}</span>
              <input id="${escapeAttr(minInputId)}" type="number" inputmode="numeric" class="input" data-range-min placeholder="${escapeAttr(String(minBound))}" value="${escapeAttr(String(minValue))}">
            </label>
            <label class="voyado-search__range-field" for="${escapeAttr(maxInputId)}">
              <span class="sr-only">${escapeHtml(`${facetLabel} Maximum`)}</span>
              <input id="${escapeAttr(maxInputId)}" type="number" inputmode="numeric" class="input" data-range-max placeholder="${escapeAttr(String(maxBound))}" value="${escapeAttr(String(maxValue))}">
            </label>
          </div>
          <button type="submit" class="button button--secondary">${escapeHtml(this._sectionConfig.rangeApplyLabel)}</button>
        </form>
      `;
    }

    return `
      <section class="voyado-search__facet-group${isOpen ? ' is-open' : ''}">
        <button type="button" class="voyado-search__facet-toggle" data-facet-toggle="${escapeAttr(facetId)}" aria-expanded="${isOpen ? 'true' : 'false'}">
          <span>${escapeHtml(facetLabel)}</span>
          <span class="voyado-search__facet-toggle-icon" aria-hidden="true">${isOpen ? '−' : '+'}</span>
        </button>
        <div class="voyado-search__facet-panel"${isOpen ? '' : ' hidden'}>
          ${content}
        </div>
      </section>
    `;
  }

  _getFacetType(facet) {
    const facetType = String(facet?.type || '').trim().toUpperCase();
    if (facetType === 'ATTRIBUTEFACET') return 'TEXT';
    if (facetType === 'RANGEFACET') return 'RANGE';
    return facetType;
  }

  _getFacetValues(facet) {
    if (Array.isArray(facet?.values)) return facet.values;
    if (Array.isArray(facet?.options)) return facet.options;
    if (this._getFacetType(facet) === 'CHECKBOX') {
      return [{
        ...facet,
        value: 'true',
        label: facet?.label ?? facet?.id ?? '',
      }];
    }
    return [];
  }

  _getRangeBounds(facet) {
    const min = facet?.min ?? facet?.options?.[0]?.min ?? facet?.selected?.min ?? '';
    const max = facet?.max ?? facet?.options?.[0]?.max ?? facet?.selected?.max ?? '';
    return { min, max };
  }

  _renderSort() {
    if (!this._sortContainer) return;

    if (!this._sectionConfig.showSort) {
      this._sortContainer.innerHTML = '';
      if (this._sortWrap) this._sortWrap.hidden = true;
      return;
    }

    const allowed = this._sectionConfig.sortOptions.length > 0
      ? this._sectionConfig.sortOptions
      : DEFAULT_SEARCH_SORTS;

    const available = this._availableSorts.length > 0
      ? this._availableSorts
        .map((value) => normalizeSortValue(value))
        .filter((value) => value && allowed.includes(value))
      : allowed;

    const currentSort = this._state.sort || 'RELEVANCE';
    const currentLabel = this._sectionConfig.sortLabels[currentSort] || currentSort;

    this._sortContainer.innerHTML = available.map((value) => {
      const label = this._sectionConfig.sortLabels[value] || value;
      const selected = value === currentSort;
      return `<button type="button" class="popover-listbox__option group" role="option" data-sort-value="${escapeAttr(value)}"${selected ? ' aria-selected="true"' : ''}><span class="reversed-link">${escapeHtml(label)}</span></button>`;
    }).join('');

    if (this._sortCurrentLabel) {
      this._sortCurrentLabel.textContent = escapeHtml(this._sectionConfig.sortLabel) + ': ' + currentLabel;
    }
    if (this._sortWrap) this._sortWrap.hidden = false;
  }

  _renderActiveFilters() {
    if (!this._activeFiltersContainer) return;

    const chips = [];

    this._facets.forEach((facet) => {
      const facetId = String(facet?.id || '');
      const facetLabel = String(facet?.label || facetId);
      const current = this._state.filters[facetId];

      if (!current) return;

      if (Array.isArray(current)) {
        const values = Array.isArray(facet?.values) ? facet.values : [];
        current.forEach((valueId) => {
          const valueMeta = values.find((value) => String(value?.id ?? value?.value ?? '') === valueId);
          const label = translateFacetLabel(String(valueMeta?.label ?? valueId), this._sectionConfig.boolTrueLabel, this._sectionConfig.boolFalseLabel);
          chips.push(`
            <button type="button" class="voyado-search__filter-chip" data-remove-filter data-facet-id="${escapeAttr(facetId)}" data-filter-value="${escapeAttr(valueId)}">
              <span>${escapeHtml(`${facetLabel}: ${label}`)}</span>
              <span aria-hidden="true">×</span>
            </button>
          `);
        });
        return;
      }

      if (current.min) {
        chips.push(`
          <button type="button" class="voyado-search__filter-chip" data-remove-filter data-facet-id="${escapeAttr(facetId)}" data-range-key="min">
            <span>${escapeHtml(`${facetLabel}: ab ${current.min}`)}</span>
            <span aria-hidden="true">×</span>
          </button>
        `);
      }

      if (current.max) {
        chips.push(`
          <button type="button" class="voyado-search__filter-chip" data-remove-filter data-facet-id="${escapeAttr(facetId)}" data-range-key="max">
            <span>${escapeHtml(`${facetLabel}: bis ${current.max}`)}</span>
            <span aria-hidden="true">×</span>
          </button>
        `);
      }
    });

    if (chips.length === 0) {
      this._activeFiltersContainer.innerHTML = '';
      this._activeFiltersContainer.hidden = true;
      return;
    }

    this._activeFiltersContainer.hidden = false;
    this._activeFiltersContainer.innerHTML = `
      <div class="voyado-search__active-filters-header">
        <span class="smallcaps">${escapeHtml(this._sectionConfig.activeFacetsLabel)}</span>
        <button type="button" class="voyado-search__clear-all link-faded" data-action="clear-all-filters">${escapeHtml(this._sectionConfig.clearAllLabel)}</button>
      </div>
      <div class="voyado-search__active-filters-list">
        ${chips.join('')}
      </div>
    `;
  }

  _renderTotalHits() {
    if (!this._totalHitsContainer) return;

    const label = this._totalHits === 1
      ? this._sectionConfig.totalHitsLabelOne
      : this._sectionConfig.totalHitsLabel.replace('{n}', String(this._totalHits));

    this._totalHitsContainer.textContent = label;
    this._announceStatus(label);
  }

  _renderPagination() {
    if (!this._pagination) return;

    if (this._sectionConfig.enableInfiniteScroll || this._totalPages <= 1) {
      this._pagination.innerHTML = '';
      this._pagination.hidden = true;
      return;
    }

    const items = this._getPaginationItems();

    this._pagination.hidden = false;
    this._pagination.innerHTML = `
      <div class="voyado-search__pagination-inner">
        <div class="voyado-search__pagination-buttons">
          ${items
            .map((item) => {
              if (item === 'ellipsis') {
                return '<span class="voyado-search__pagination-ellipsis">…</span>';
              }

              return `<button type="button" class="voyado-search__pagination-button${item === this._currentPage ? ' is-current' : ''}" data-page="${escapeAttr(String(item))}"${item === this._currentPage ? ' aria-current="page"' : ''}>${escapeHtml(String(item))}</button>`;
            })
            .join('')}
        </div>
      </div>
    `;
  }

  _getPaginationItems() {
    const total = this._totalPages;
    const current = this._currentPage;

    const pageSet = new Set([
      1,
      total,
      Math.max(1, current - 1),
      current,
      Math.min(total, current + 1),
    ]);

    const sorted = Array.from(pageSet).sort((a, b) => a - b);

    const items = [];
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) {
        items.push('ellipsis');
      }
      items.push(page);
    });

    return items;
  }

  _connectObserver() {
    if (!this._sectionConfig.enableInfiniteScroll || !this._sentinel || this._observer) return;

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (this._isFetching) return;
        if (!this._hasNextPage) return;

        this._fetchPage(this._currentPage + 1, true);
      });
    }, { rootMargin: '200px 0px' });

    this._observer.observe(this._sentinel);
  }

  _disconnectObserver() {
    if (!this._observer) return;
    this._observer.disconnect();
    this._observer = null;
  }

  _abortInFlight() {
    if (!this._abortController) return;
    this._abortController.abort();
    this._abortController = null;
  }

  _setLoading(isLoading, gridEmpty) {
    document.documentElement.dispatchEvent(new CustomEvent(isLoading ? 'theme:loading:start' : 'theme:loading:end'));
    this._isLoadingVisible = isLoading && gridEmpty;
    this._announceStatus(isLoading ? this._sectionConfig.loadingLabel : '');

    if (this._loading) {
      this._loading.hidden = !this._isLoadingVisible;
    }
  }

  _clearResults() {
    this._activeTab = 'products';
    this._lastContentCount = 0;
    this._contentItems = [];
    this._activeContentType = 'all';
    this._hasContentSubtabs = false;
    this._isLoadingVisible = false;
    this._isShowingNoResults = false;
    this._isShowingError = false;
    this._grid.innerHTML = '';
    this._grid.hidden = false;
    this._sentinel.hidden = true;
    if (this._recentSearches) {
      this._recentSearches.innerHTML = '';
      this._recentSearches.hidden = true;
    }
    if (this._recentlyViewed) {
      this._recentlyViewed.innerHTML = '';
      this._recentlyViewed.hidden = true;
    }
    if (this._secondaryList) {
      this._secondaryList.innerHTML = '';
      this._secondaryList.hidden = true;
    }
    if (this._contentList) {
      this._contentList.innerHTML = '';
      this._contentList.hidden = true;
    }
    if (this._relatedSearches) {
      this._relatedSearches.innerHTML = '';
      this._relatedSearches.hidden = true;
    }
    if (this._tabs) {
      this._tabs.innerHTML = '';
      this._tabs.hidden = true;
    }
    if (this._didYouMean) {
      this._didYouMean.innerHTML = '';
      this._didYouMean.hidden = true;
    }
    this._applyTabVisibility();
  }

  _clearUiStates() {
    this._isLoadingVisible = false;
    this._isShowingNoResults = false;
    this._isShowingError = false;
    if (this._loading) this._loading.hidden = true;
    if (this._noResults) this._noResults.hidden = true;
    if (this._error) this._error.hidden = true;
    this._renderHeaderState();
  }

  _showNoResults() {
    this._isShowingNoResults = true;
    this._announceStatus(this._sectionConfig.noResultsLabel);
    if (this._noResults) this._noResults.hidden = false;
    if (this._searchForm) {
      this._searchForm.hidden = !this._sectionConfig.noResultsShowSearchBar;
    }
  }

  _showError() {
    this._clearResults();
    this._isShowingError = true;
    this._announceStatus(this._sectionConfig.errorLabel);
    if (this._error) {
      this._error.hidden = false;
      this._error.innerHTML = `
        <div class="prose prose--tight">
          <h2 class="h4">${escapeHtml(this._sectionConfig.errorLabel)}</h2>
          <p>${escapeHtml(this._sectionConfig.errorSubtext)}</p>
        </div>
      `;
    }
    this._renderPagination();
  }

  _announceStatus(message) {
    if (this._status) {
      this._status.textContent = message;
    }
  }

  _getRedirectUrl(data) {
    if (!this._sectionConfig.enableRedirect) return '';

    const redirectUrl = String(data?.redirect?.url || '').trim();
    if (!redirectUrl) return '';

    try {
      const parsedUrl = new URL(redirectUrl, window.location.origin);
      if (parsedUrl.origin !== window.location.origin) return '';
      return parsedUrl.href;
    } catch {
      return '';
    }
  }

  _getDidYouMeanSuggestion(data) {
    const candidates = [
      data?.correction?.correctedQuery,
      data?.correction?.query,
      data?.spellingSuggestions?.[0]?.correctedQuery,
      data?.spellingSuggestions?.[0]?.query,
      data?.spellingSuggestions?.[0]?.q,
      data?.didYouMean?.[0]?.q,
      data?.didYouMean?.[0]?.query,
      data?.autoCorrect?.q,
    ];

    for (const candidate of candidates) {
      const suggestion = String(candidate || '').trim();
      if (suggestion && suggestion !== this._state.query) {
        return suggestion;
      }
    }

    return '';
  }

  _getDYMOrigin(data) {
    const dym = String(data?.didYouMean?.[0]?.q ?? data?.didYouMean?.[0]?.query ?? '').trim();
    if (dym && dym !== this._state.query) return 'DID_YOU_MEAN';
    return 'UNDO_AUTO_CORRECT';
  }

  _renderDidYouMean(suggestion, originalQuery, dymOrigin = 'UNDO_AUTO_CORRECT') {
    if (!this._didYouMean) return;

    if (!this._sectionConfig.enableDidYouMean || !suggestion || this._state.bypassCorrection) {
      this._didYouMean.innerHTML = '';
      this._didYouMean.hidden = true;
      return;
    }

    if (dymOrigin === 'DID_YOU_MEAN') {
      const dymLabel = (this._sectionConfig.didYouMeanLinkLabel || 'Meinten Sie: {suggestion}?')
        .replace('{suggestion}', suggestion);
      const dymUrl = escapeAttr(safeUrl(`/search?${new URLSearchParams({ q: suggestion, type: 'product', origin: 'DID_YOU_MEAN' }).toString()}`));
      this._didYouMean.hidden = false;
      this._didYouMean.innerHTML = `
        <p class="voyado-search__did-you-mean">
          <a href="${dymUrl}" class="link">${escapeHtml(dymLabel)}</a>
        </p>
      `;
      return;
    }

    const noticeLabel = this._sectionConfig.didYouMeanLabel
      .replace('{suggestion}', suggestion)
      .replace('{original}', originalQuery || '');

    const showLink = this._sectionConfig.enableOriginalQueryLink && originalQuery;
    const linkLabel = showLink
      ? this._sectionConfig.originalQueryLinkLabel.replace('{original}', originalQuery)
      : '';

    this._didYouMean.hidden = false;
    this._didYouMean.innerHTML = `
      <p class="voyado-search__autocorrection-notice">
        ${escapeHtml(noticeLabel)}
        ${showLink ? `<button type="button" class="voyado-search__bypass-correction link-faded" data-bypass-correction>${escapeHtml(linkLabel)}</button>` : ''}
      </p>
    `;
  }

  _renderSecondaryList(secondaryList, primaryHits) {
    if (!this._secondaryList) return;

    const threshold = this._sectionConfig.secondaryListThreshold;
    const shouldShowForHits = primaryHits <= threshold;
    const sourceGroups = Array.isArray(secondaryList?.productGroups)
      ? secondaryList.productGroups
      : Array.isArray(secondaryList)
        ? secondaryList
        : [];
    const productGroups = sourceGroups.filter((group) => this._getGroupProducts(group).length > 0);

    if (!this._sectionConfig.enableSecondaryList || !shouldShowForHits || productGroups.length === 0) {
      this._secondaryList.innerHTML = '';
      this._secondaryList.hidden = true;
      return;
    }

    const cardsHtml = productGroups
      .map((group) => this._renderProductCard(group))
      .filter(Boolean)
      .join('');

    if (!cardsHtml) {
      this._secondaryList.innerHTML = '';
      this._secondaryList.hidden = true;
      return;
    }

    const viewAllUrl = escapeAttr(safeUrl(`/search?${new URLSearchParams({ q: this._state.query, type: 'product', viewAllSecondary: 'true' }).toString()}`));
    const viewAllLabel = escapeHtml(this._sectionConfig.secondaryListViewAllLabel || 'Alle ähnlichen Produkte anzeigen');

    this._secondaryList.hidden = false;
    this._secondaryList.innerHTML = `
      <section class="voyado-search__secondary-results">
        <div class="prose prose--tight">
          ${this._sectionConfig.secondaryListLabel ? `<h2 class="h4">${escapeHtml(this._sectionConfig.secondaryListLabel)}</h2>` : ''}
          ${this._sectionConfig.secondaryListSubtext ? `<p>${escapeHtml(this._sectionConfig.secondaryListSubtext)}</p>` : ''}
        </div>
        <div class="voyado-search__grid">
          ${cardsHtml}
        </div>
        <div class="voyado-search__secondary-view-all">
          <a href="${viewAllUrl}" class="button">${viewAllLabel}</a>
        </div>
      </section>
    `;
  }

  _getSecondaryList(data, primaryList) {
    return data?.secondaryList || primaryList?.secondaryList || data?.productGroups?.secondaryList || null;
  }

  _renderRecentSearches(data) {
    if (!this._recentSearches) return;

    if (!this._sectionConfig.enableRecentSearches) {
      this._recentSearches.innerHTML = '';
      this._recentSearches.hidden = true;
      return;
    }

    const recentSearches = Array.isArray(data?.phrases) && data.phrases.length > 0
      ? data.phrases
      : Array.isArray(data?.recentSearches)
        ? data.recentSearches
        : [];
    const trendingPhrases = Array.isArray(data?.trendingPhrases) && data.trendingPhrases.length > 0
      ? data.trendingPhrases
      : Array.isArray(data?.trending)
        ? data.trending
        : [];
    const items = (recentSearches.length > 0 ? recentSearches : trendingPhrases)
      .filter(Boolean)
      .slice(0, this._sectionConfig.recentSearchesMax);

    if (items.length === 0) {
      this._recentSearches.innerHTML = '';
      this._recentSearches.hidden = true;
      return;
    }

    const label = recentSearches.length > 0
      ? this._sectionConfig.recentSearchesLabel
      : this._sectionConfig.trendingLabel;

    this._recentSearches.hidden = false;
    this._recentSearches.innerHTML = `
      <section class="voyado-search__secondary-results">
        ${label ? `<h2 class="h4">${escapeHtml(label)}</h2>` : ''}
        <div class="voyado-search__recent-search-items">
          ${items
            .map((item) => {
              const phrase = typeof item === 'string'
                ? item
                : String(item?.phrase ?? item?.q ?? item?.query ?? '');
              const highlighted = this._sanitiseHighlight(
                typeof item === 'string'
                  ? item
                  : String(item?.highlighted ?? phrase)
              );
              const phraseUrl = safeUrl(`/search?${new URLSearchParams({ q: phrase, type: 'product', origin: 'RECENT_SEARCH' }).toString()}`);

              if (!phrase) return '';

              return `
                <a
                  href="${escapeAttr(phraseUrl)}"
                  class="button button--secondary"
                  data-action="recent-search"
                >
                  ${highlighted}
                </a>
              `;
            })
            .join('')}
        </div>
      </section>
    `;
  }

  _renderRecentlyViewed(data) {
    if (!this._recentlyViewed) return;

    if (!this._sectionConfig.enableRecentlyViewed) {
      this._recentlyViewed.innerHTML = '';
      this._recentlyViewed.hidden = true;
      return;
    }

    const groups = Array.isArray(data?.recentlyViewedProductGroups) && data.recentlyViewedProductGroups.length > 0
      ? data.recentlyViewedProductGroups
      : Array.isArray(data?.recentlyViewed?.productGroups) && data.recentlyViewed.productGroups.length > 0
        ? data.recentlyViewed.productGroups
        : Array.isArray(data?.recentlyViewedProducts)
          ? data.recentlyViewedProducts
          : [];
    const productGroups = groups
      .map((group) => (Array.isArray(group?.products) ? group : { products: [group] }))
      .filter((group) => this._getGroupProducts(group).length > 0)
      .slice(0, this._sectionConfig.recentlyViewedMax);

    if (productGroups.length === 0) {
      this._recentlyViewed.innerHTML = '';
      this._recentlyViewed.hidden = true;
      return;
    }

    const cardsHtml = productGroups
      .map((group) => this._renderProductCard(group))
      .filter(Boolean)
      .join('');

    if (!cardsHtml) {
      this._recentlyViewed.innerHTML = '';
      this._recentlyViewed.hidden = true;
      return;
    }

    this._recentlyViewed.hidden = false;
    this._recentlyViewed.innerHTML = `
      <section class="voyado-search__secondary-results">
        ${this._sectionConfig.recentlyViewedLabel ? `<h2 class="h4">${escapeHtml(this._sectionConfig.recentlyViewedLabel)}</h2>` : ''}
        <div class="voyado-search__recently-viewed-list">
          ${cardsHtml}
        </div>
      </section>
    `;
  }

  _renderContentList(data) {
    if (!this._contentList || !this._sectionConfig.enableContentList) {
      if (this._contentList) {
        this._contentList.innerHTML = '';
        this._contentList.hidden = true;
      }
      this._renderTabs(0);
      return;
    }

    const contentLists = Array.isArray(data?.contentLists)
      ? data.contentLists
      : Array.isArray(data?.contentGroups)
        ? data.contentGroups
        : [];
    const items = contentLists
      .flatMap((list) => (Array.isArray(list?.items) ? list.items : Array.isArray(list?.contentItems) ? list.contentItems : []))
      .filter(Boolean);

    if (items.length === 0) {
      this._contentList.innerHTML = '';
      this._contentList.hidden = true;
      this._contentItems = [];
      this._renderTabs(0);
      return;
    }

    // Store full items for client-side sub-tab filtering
    this._contentItems = items;
    this._activeContentType = 'all';

    // Extract distinct content types for sub-tabs
    const types = [...new Set(items.map((item) => String(item.type ?? item.contentType ?? '')).filter(Boolean))];
    this._hasContentSubtabs = types.length >= 2;

    const typeLabels = this._sectionConfig.contentTypeLabels;
    const subtabsHtml = this._hasContentSubtabs
      ? `<div class="voyado-search__content-subtabs" role="tablist">
          <button class="voyado-search__content-subtab voyado-search__content-subtab--active" data-content-type="all" role="tab" aria-selected="true">Alle</button>
          ${types.map((type) => `<button class="voyado-search__content-subtab" data-content-type="${escapeAttr(type)}" role="tab" aria-selected="false">${escapeHtml(typeLabels[type] || type)}</button>`).join('')}
        </div>`
      : '';

    this._contentList.innerHTML = `
      <section class="voyado-search__secondary-results">
        ${this._sectionConfig.contentListLabel ? `<h2 class="h4">${escapeHtml(this._sectionConfig.contentListLabel)}</h2>` : ''}
        ${subtabsHtml}
        <div class="voyado-search__content-cards-wrapper">
          ${this._renderContentCards(items)}
        </div>
      </section>
    `;
    this._contentList.hidden = false;
    this._renderTabs(items.length);
  }

  _renderContentCards(items) {
    const filtered = this._activeContentType === 'all'
      ? items
      : items.filter((item) => String(item.type ?? item.contentType ?? '') === this._activeContentType);

    if (filtered.length === 0) {
      return '<p class="voyado-search__content-empty"></p>';
    }

    const cardItems = filtered.map((item) => {
      const title = String(item.title ?? item.name ?? '');
      const link = safeUrl(item.link ?? item.url ?? '#');
      const ticket = String(item.ticket ?? '');
      const imageObj = item.image;
      const imageUrl = (imageObj && typeof imageObj === 'object'
        ? (imageObj.sources?.[0]?.url ?? imageObj.sources?.[0] ?? imageObj.url ?? '')
        : typeof imageObj === 'string' ? imageObj : '')
        || item.imageUrl
        || item.thumbnailUrl
        || '';
      const type = String(item.type ?? item.contentType ?? '');
      const rawDescription = String(item.description ?? item.excerpt ?? item.summary ?? '');
      const description = rawDescription.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

      const imageHtml = imageUrl
        ? `<div class="voyado-search__content-card-media">
            <img src="${escapeAttr(safeUrl(imageUrl))}" alt="${escapeAttr(title)}" loading="lazy" width="400" height="225">
          </div>`
        : '';

      const typeLabel = this._sectionConfig.contentTypeLabels[type] || type;
      const typeBadgeHtml = type
        ? `<span class="voyado-search__content-card-type">${escapeHtml(typeLabel)}</span>`
        : '';

      const descHtml = description
        ? `<p class="voyado-search__content-card-desc">${escapeHtml(description)}</p>`
        : '';

      return `
        <li class="voyado-search__content-item voyado-search__content-card">
          <a href="${escapeAttr(link)}" class="voyado-search__content-card-link" data-voyado-ticket="${escapeAttr(ticket)}">
            ${imageHtml}
            <div class="voyado-search__content-card-body">
              ${typeBadgeHtml}
              <span class="voyado-search__content-card-title">${escapeHtml(title)}</span>
              ${descHtml}
            </div>
          </a>
        </li>
      `;
    });

    return `<ul class="voyado-search__content-cards">${cardItems.join('')}</ul>`;
  }

  _renderRelatedSearches(data) {
    if (!this._relatedSearches) return;

    if (!this._sectionConfig.enableRelatedSearches) {
      this._relatedSearches.innerHTML = '';
      this._relatedSearches.hidden = true;
      return;
    }

    const items = Array.isArray(data?.relatedSearches)
      ? data.relatedSearches
          .map((item) => typeof item === 'string' ? item : String(item?.q ?? item?.query ?? item?.phrase ?? ''))
          .filter(Boolean)
          .slice(0, this._sectionConfig.relatedSearchesMax)
      : [];

    if (items.length === 0) {
      this._relatedSearches.innerHTML = '';
      this._relatedSearches.hidden = true;
      return;
    }

    this._relatedSearches.hidden = false;
    this._relatedSearches.innerHTML = `
      <div class="voyado-search__related-searches-bar">
        ${this._sectionConfig.relatedSearchesLabel ? `<span class="voyado-search__related-searches-label smallcaps">${escapeHtml(this._sectionConfig.relatedSearchesLabel)}</span>` : ''}
        <div class="voyado-search__related-searches">
          ${items
            .map((item) => `<a href="${escapeAttr(safeUrl(`/search?${new URLSearchParams({ q: item, type: 'product', origin: 'RELATED_SEARCHES' }).toString()}`))}" class="voyado-search__related-tag">${escapeHtml(item)}</a>`)
            .join('')}
        </div>
      </div>
    `;
  }

  _renderTabs(contentCount) {
    this._lastContentCount = contentCount;

    if (!this._tabs || !this._sectionConfig.enableContentList || contentCount === 0) {
      if (this._tabs) {
        this._tabs.innerHTML = '';
        this._tabs.hidden = true;
      }
      this._applyTabVisibility();
      return;
    }

    const showContentBadge = this._sectionConfig.contentTabBadge && contentCount > 0;
    const showProductsBadge = this._sectionConfig.contentTabBadge && this._totalHits > 0;

    this._tabs.hidden = false;
    this._tabs.innerHTML = `
      <div class="voyado-search__tabs" role="tablist">
        <button type="button" role="tab" class="voyado-search__tab${this._activeTab === 'products' ? ' is-active' : ''}" data-tab="products" aria-selected="${this._activeTab === 'products' ? 'true' : 'false'}">
          ${escapeHtml(this._sectionConfig.productsTabLabel)}${showProductsBadge ? ` <span class="voyado-search__tab-badge">${escapeHtml(String(this._totalHits))}</span>` : ''}
        </button>
        <button type="button" role="tab" class="voyado-search__tab${this._activeTab === 'content' ? ' is-active' : ''}" data-tab="content" aria-selected="${this._activeTab === 'content' ? 'true' : 'false'}">
          ${escapeHtml(this._sectionConfig.contentTabLabel)}${showContentBadge ? ` <span class="voyado-search__tab-badge">${escapeHtml(String(contentCount))}</span>` : ''}
        </button>
      </div>
    `;

    this._applyTabVisibility();
  }

  _applyTabVisibility() {
    const showContent = this._activeTab === 'content' && this._lastContentCount > 0;
    const hasTabs = this._lastContentCount > 0;
    if (this._activeFiltersContainer) this._activeFiltersContainer.hidden = showContent || this._activeFiltersContainer.innerHTML === '';
    if (this._toolbar) this._toolbar.hidden = showContent;
    if (this._totalHitsContainer) this._totalHitsContainer.hidden = showContent || hasTabs || !this._totalHitsContainer.textContent;
    if (this._grid) this._grid.hidden = showContent;
    if (this._sentinel) {
      this._sentinel.hidden = showContent || !(
        this._sectionConfig.enableInfiniteScroll &&
        this._currentPage < this._totalPages &&
        this._grid.childElementCount > 0
      );
    }
    if (this._pagination) this._pagination.hidden = showContent || this._pagination.innerHTML === '';
    if (this._secondaryList) this._secondaryList.hidden = showContent || this._secondaryList.innerHTML === '';
    if (this._relatedSearches) this._relatedSearches.hidden = this._relatedSearches.innerHTML === '';
    if (this._loading) this._loading.hidden = showContent || !this._isLoadingVisible;
    if (this._noResults) this._noResults.hidden = showContent || !this._isShowingNoResults;
    if (this._error) this._error.hidden = showContent || !this._isShowingError;
    if (this._didYouMean) this._didYouMean.hidden = showContent || this._didYouMean.innerHTML === '';
    if (this._facetsContainer) this._facetsContainer.hidden = showContent || this._facetsContainer.innerHTML === '';
    if (this._contentList) this._contentList.hidden = !showContent;
  }

  _isFacetOpen(facetId) {
    if (typeof this._facetOpenState[facetId] === 'boolean') {
      return this._facetOpenState[facetId];
    }

    const current = this._state.filters[facetId];
    const hasActiveValue = Array.isArray(current)
      ? current.length > 0
      : !!(current && (current.min || current.max));

    this._facetOpenState[facetId] = hasActiveValue;
    return hasActiveValue;
  }

  _focusResultsIfNeeded() {
    if (!this._shouldMoveFocus) return;

    const focusTarget = this._resultsSummaryText || this._totalHitsContainer || this._noResults || this._error;
    if (focusTarget instanceof HTMLElement) {
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    }

    this._shouldMoveFocus = false;
  }
}

if (!window.__voyadoSearchPageInitialized) {
  window.__voyadoSearchPageInitialized = true;
  window.__voyadoSearchPageInstance = new VoyadoSearchPage();
}

document.addEventListener('visitorConsentCollected', () => {
  if (!_loadedWithoutConsent) return;
  _loadedWithoutConsent = false;
  window.__voyadoSearchPageInstance?._loadFromState('replace');
}, { once: true });
